import * as THREE from 'three';
import {
	createPolygonFromGridPoints,
	hidePolygonOutline,
	showPolygonFill,
	showPolygonOutline,
} from './visualization';
import { useDailySunlightStore } from '@/store/dailySunlightsStore';

export interface GridPoint {
	x: number;
	y: number;
	z: number;
}
const selectionHighlights = new Map<string, THREE.Mesh>();

const pointToKey = (point: GridPoint): string => {
	return `${point.x},${point.y},${point.z}`;
};

export const keyToPoint = (key: string): GridPoint => {
	const [x, y, z] = key.split(',').map(Number);
	return { x, y, z };
};

export const handleGridPointClick = (
	position: THREE.Vector3,
	gridSize: number,
	scene: THREE.Scene
) => {
	const point: GridPoint = {
		x: position.x,
		y: position.y,
		z: position.z,
	};

	const selectedGridPoints = useDailySunlightStore.getState().selectedGridPoints;
	const pointKey = pointToKey(point);

	if (selectedGridPoints.some((p) => pointToKey(p) === pointKey)) {
		const newSelectedGridPoints = selectedGridPoints.filter((p) => pointToKey(p) !== pointKey);
		useDailySunlightStore.getState().setSelectedGridPoints(newSelectedGridPoints);
		removeGridPointSelection(pointKey, scene);
	} else {
		const newSelectedGridPoints = [...selectedGridPoints, point];
		useDailySunlightStore.getState().setSelectedGridPoints(newSelectedGridPoints);
		showGridPointSelection(position, gridSize, scene, pointKey);
	}
};

const showGridPointSelection = (
	position: THREE.Vector3,
	gridSize: number,
	scene: THREE.Scene,
	pointKey: string
) => {
	const planeGeometry = new THREE.PlaneGeometry(gridSize, gridSize);

	const planeMaterial = new THREE.MeshBasicMaterial({
		color: 0x00ff00, // Green for selection (different from white hover)
		side: THREE.DoubleSide,
		transparent: true,
		opacity: 0.6,
		depthTest: false,
		depthWrite: false,
	});

	const plane = new THREE.Mesh(planeGeometry, planeMaterial);

	plane.position.copy(position);
	plane.position.z += 0.07;

	const edgesGeometry = new THREE.EdgesGeometry(planeGeometry);
	const edgesMaterial = new THREE.LineBasicMaterial({
		color: 0x00ff00,
		linewidth: 2,
		transparent: true,
		opacity: 1.0,
		depthTest: false,
		depthWrite: false,
	});
	const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial);
	plane.add(edges);

	plane.userData.isGridSelection = true;
	plane.renderOrder = 1001;

	selectionHighlights.set(pointKey, plane);
	scene.add(plane);
	hidePolygonOutline(scene);
	updatePolygonOutline(scene, getSelectedGridPoints());
};

const removeGridPointSelection = (pointKey: string, scene: THREE.Scene) => {
	const highlight = selectionHighlights.get(pointKey);
	if (highlight) {
		scene.remove(highlight);

		highlight.children.forEach((child) => {
			if (child instanceof THREE.LineSegments) {
				child.geometry.dispose();
				(child.material as THREE.Material).dispose();
			}
		});

		if (highlight.geometry) {
			highlight.geometry.dispose();
		}
		if (highlight.material) {
			(highlight.material as THREE.Material).dispose();
		}

		selectionHighlights.delete(pointKey);

		updatePolygonOutline(scene, getSelectedGridPoints());
	}
};

export const clearAllSelections = (scene: THREE.Scene) => {
	const selectedGridPoints = useDailySunlightStore.getState().selectedGridPoints;
	selectedGridPoints.forEach((point) => {
		const pointKey = pointToKey(point);
		removeGridPointSelection(pointKey, scene);
	});
	useDailySunlightStore.getState().setSelectedGridPoints([]);
	selectionHighlights.clear();
};

export const getSelectedGridPoints = (): GridPoint[] => {
	return useDailySunlightStore.getState().selectedGridPoints.map((point) => {
		const [x, y, z] = pointToKey(point).split(',').map(Number);
		return { x, y, z };
	});
};

const updatePolygonOutline = (scene: THREE.Scene, selectedCells: GridPoint[]) => {
	hidePolygonOutline(scene);
	const boundaryPolygon = createPolygonFromGridPoints(
		selectedCells,
		useDailySunlightStore.getState().gridSize
	);
	showPolygonOutline(scene, boundaryPolygon);
	showPolygonFill(scene, boundaryPolygon);
};
